// src/features/analytics/components/AdvancedCharts/AdvancedCharts.jsx
import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Select, Statistic, Tag, Alert, List, Progress } from 'antd';
import {
    TrendingUpOutlined,
    TrendingDownOutlined,
    MinusOutlined,
    RiseOutlined,
    FallOutlined
} from '@ant-design/icons';
import CalendarHeatmap from 'react-calendar-heatmap';
import { Line, Bar } from 'react-chartjs-2';
import { advancedChartsService } from '../../../../shared/services/advancedChartsService';
import { formatCurrency } from '../../../../shared/utils/formatters';
import dayjs from 'dayjs';
import 'react-calendar-heatmap/dist/styles.css';
import styles from './AdvancedCharts.module.css';

const { Option } = Select;

export default function AdvancedCharts({ debts, payments }) {
    const [selectedChart, setSelectedChart] = useState('heatmap');

    // Tính toán dữ liệu
    const heatmapData = useMemo(() =>
        advancedChartsService.generateHeatmapData(debts, payments),
        [debts, payments]
    );

    const trendAnalysis = useMemo(() =>
        advancedChartsService.analyzeTrends(debts, payments),
        [debts, payments]
    );

    const monthComparison = useMemo(() =>
        advancedChartsService.compareWithLastMonth(debts, payments),
        [debts, payments]
    );

    const cashFlowForecast = useMemo(() =>
        advancedChartsService.forecastCashFlow(debts, payments),
        [debts, payments]
    );

    // Render bản đồ nhiệt
    const renderHeatmap = () => (
        <Card title="🗓️ Bản đồ nhiệt hoạt động tài chính" className={styles.chartCard}>
            <div className={styles.heatmapContainer}>
                <CalendarHeatmap
                    startDate={dayjs().subtract(12, 'month').toDate()}
                    endDate={dayjs().toDate()}
                    values={heatmapData}
                    classForValue={(value) => {
                        if (!value || value.count === 0) return 'color-empty';
                        if (value.value > 0) return `color-positive-${Math.min(4, Math.ceil(value.count / 2))}`;
                        return `color-negative-${Math.min(4, Math.ceil(value.count / 2))}`;
                    }}
                    tooltipDataAttrs={(value) => ({
                        'data-tip': value.date ?
                            `${dayjs(value.date).format('DD/MM/YYYY')}<br/>
               Nợ mới: ${formatCurrency(value.newDebts || 0)}<br/>
               Thanh toán: ${formatCurrency(value.payments || 0)}<br/>
               Net: ${formatCurrency(value.value || 0)}` :
                            'Không có dữ liệu'
                    })}
                    showWeekdayLabels={true}
                />
            </div>

            <div className={styles.heatmapLegend}>
                <span>Ít hoạt động</span>
                <div className={styles.legendColors}>
                    <div className="color-empty"></div>
                    <div className="color-positive-1"></div>
                    <div className="color-positive-2"></div>
                    <div className="color-positive-3"></div>
                    <div className="color-positive-4"></div>
                </div>
                <span>Nhiều hoạt động</span>
            </div>
        </Card>
    );

    // Render phân tích xu hướng
    const renderTrendAnalysis = () => {
        const getTrendIcon = (direction) => {
            switch (direction) {
                case 'increasing': return <RiseOutlined style={{ color: '#ff4d4f' }} />;
                case 'decreasing': return <FallOutlined style={{ color: '#52c41a' }} />;
                default: return <MinusOutlined style={{ color: '#faad14' }} />;
            }
        };

        const getTrendColor = (direction, isDebt = false) => {
            if (direction === 'stable') return '#faad14';
            if (isDebt) {
                return direction === 'increasing' ? '#ff4d4f' : '#52c41a';
            } else {
                return direction === 'increasing' ? '#52c41a' : '#ff4d4f';
            }
        };

        const trendChartData = {
            labels: trendAnalysis.monthlyData.map(m => m.month),
            datasets: [
                {
                    label: 'Nợ mới',
                    data: trendAnalysis.monthlyData.map(m => m.newDebts),
                    borderColor: '#ff4d4f',
                    backgroundColor: 'rgba(255, 77, 79, 0.1)',
                    tension: 0.4
                },
                {
                    label: 'Thanh toán',
                    data: trendAnalysis.monthlyData.map(m => m.payments),
                    borderColor: '#52c41a',
                    backgroundColor: 'rgba(82, 196, 26, 0.1)',
                    tension: 0.4
                }
            ]
        };

        return (
            <Card title="📈 Phân tích xu hướng" className={styles.chartCard}>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={8}>
                        <Card size="small">
                            <Statistic
                                title="Xu hướng nợ mới"
                                value={Math.abs(trendAnalysis.debt.percentage)}
                                suffix="%"
                                prefix={getTrendIcon(trendAnalysis.debt.direction)}
                                valueStyle={{ color: getTrendColor(trendAnalysis.debt.direction, true) }}
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                {trendAnalysis.debt.direction === 'increasing' ? 'Tăng' :
                                    trendAnalysis.debt.direction === 'decreasing' ? 'Giảm' : 'Ổn định'}
                            </div>
                        </Card>
                    </Col>

                    <Col span={8}>
                        <Card size="small">
                            <Statistic
                                title="Xu hướng thanh toán"
                                value={Math.abs(trendAnalysis.payment.percentage)}
                                suffix="%"
                                prefix={getTrendIcon(trendAnalysis.payment.direction)}
                                valueStyle={{ color: getTrendColor(trendAnalysis.payment.direction, false) }}
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                {trendAnalysis.payment.direction === 'increasing' ? 'Tăng' :
                                    trendAnalysis.payment.direction === 'decreasing' ? 'Giảm' : 'Ổn định'}
                            </div>
                        </Card>
                    </Col>

                    <Col span={8}>
                        <Card size="small">
                            <Statistic
                                title="Xu hướng số dư"
                                value={Math.abs(trendAnalysis.balance.percentage)}
                                suffix="%"
                                prefix={getTrendIcon(trendAnalysis.balance.direction)}
                                valueStyle={{ color: getTrendColor(trendAnalysis.balance.direction, true) }}
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                {trendAnalysis.balance.direction === 'increasing' ? 'Tăng' :
                                    trendAnalysis.balance.direction === 'decreasing' ? 'Giảm' : 'Ổn định'}
                            </div>
                        </Card>
                    </Col>
                </Row>

                <div style={{ height: 300 }}>
                    <Line
                        data={trendChartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                title: {
                                    display: true,
                                    text: 'Xu hướng nợ và thanh toán theo tháng'
                                }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: function (value) {
                                            return formatCurrency(value);
                                        }
                                    }
                                }
                            }
                        }}
                    />
                </div>
            </Card>
        );
    };

    // Render so sánh tháng
    const renderMonthComparison = () => {
        const getChangeIcon = (percent) => {
            if (percent > 5) return <RiseOutlined style={{ color: '#ff4d4f' }} />;
            if (percent < -5) return <FallOutlined style={{ color: '#52c41a' }} />;
            return <MinusOutlined style={{ color: '#faad14' }} />;
        };

        const comparisonData = {
            labels: ['Tháng trước', 'Tháng này'],
            datasets: [
                {
                    label: 'Nợ mới',
                    data: [monthComparison.newDebts.last, monthComparison.newDebts.current],
                    backgroundColor: ['rgba(255, 77, 79, 0.6)', 'rgba(255, 77, 79, 0.8)'],
                    borderColor: ['#ff4d4f', '#ff4d4f'],
                    borderWidth: 1
                },
                {
                    label: 'Thanh toán',
                    data: [monthComparison.payments.last, monthComparison.payments.current],
                    backgroundColor: ['rgba(82, 196, 26, 0.6)', 'rgba(82, 196, 26, 0.8)'],
                    borderColor: ['#52c41a', '#52c41a'],
                    borderWidth: 1
                }
            ]
        };

        return (
            <Card title="📊 So sánh với tháng trước" className={styles.chartCard}>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={12}>
                        <Card size="small">
                            <Statistic
                                title="Thay đổi nợ mới"
                                value={Math.abs(monthComparison.newDebts.changePercent)}
                                suffix="%"
                                prefix={getChangeIcon(monthComparison.newDebts.changePercent)}
                                valueStyle={{
                                    color: monthComparison.newDebts.changePercent > 0 ? '#ff4d4f' : '#52c41a'
                                }}
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                {formatCurrency(monthComparison.newDebts.current)} (hiện tại)
                            </div>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card size="small">
                            <Statistic
                                title="Thay đổi thanh toán"
                                value={Math.abs(monthComparison.payments.changePercent)}
                                suffix="%"
                                prefix={getChangeIcon(monthComparison.payments.changePercent)}
                                valueStyle={{
                                    color: monthComparison.payments.changePercent > 0 ? '#52c41a' : '#ff4d4f'
                                }}
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                {formatCurrency(monthComparison.payments.current)} (hiện tại)
                            </div>
                        </Card>
                    </Col>
                </Row>

                <div style={{ height: 300 }}>
                    <Bar
                        data={comparisonData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                title: {
                                    display: true,
                                    text: 'So sánh nợ mới và thanh toán'
                                }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: function (value) {
                                            return formatCurrency(value);
                                        }
                                    }
                                }
                            }
                        }}
                    />
                </div>
            </Card>
        );
    };

    // Render dự báo dòng tiền
    const renderCashFlowForecast = () => {
        const forecastData = {
            labels: cashFlowForecast.forecast.map(f => f.month),
            datasets: [
                {
                    label: 'Dự báo nợ mới',
                    data: cashFlowForecast.forecast.map(f => f.newDebts),
                    borderColor: '#ff4d4f',
                    backgroundColor: 'rgba(255, 77, 79, 0.1)',
                    borderDash: [5, 5],
                    tension: 0.4
                },
                {
                    label: 'Dự báo thanh toán',
                    data: cashFlowForecast.forecast.map(f => f.payments),
                    borderColor: '#52c41a',
                    backgroundColor: 'rgba(82, 196, 26, 0.1)',
                    borderDash: [5, 5],
                    tension: 0.4
                },
                {
                    label: 'Dự báo số dư nợ',
                    data: cashFlowForecast.forecast.map(f => f.balance),
                    borderColor: '#1890ff',
                    backgroundColor: 'rgba(24, 144, 255, 0.1)',
                    borderDash: [10, 5],
                    tension: 0.4,
                    yAxisID: 'y1'
                }
            ]
        };

        return (
            <Card title="🔮 Dự báo dòng tiền" className={styles.chartCard}>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={12}>
                        <Alert
                            message={`Độ tin cậy: ${cashFlowForecast.confidence}%`}
                            type={cashFlowForecast.confidence > 70 ? 'success' : 'warning'}
                            showIcon
                        />
                    </Col>
                    <Col span={12}>
                        <Progress
                            percent={cashFlowForecast.confidence}
                            size="small"
                            strokeColor={cashFlowForecast.confidence > 70 ? '#52c41a' : '#faad14'}
                        />
                    </Col>
                </Row>

                <div style={{ height: 300, marginBottom: 16 }}>
                    <Line
                        data={forecastData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                title: {
                                    display: true,
                                    text: 'Dự báo 6 tháng tới'
                                }
                            },
                            scales: {
                                y: {
                                    type: 'linear',
                                    display: true,
                                    position: 'left',
                                    beginAtZero: true,
                                    ticks: {
                                        callback: function (value) {
                                            return formatCurrency(value);
                                        }
                                    }
                                },
                                y1: {
                                    type: 'linear',
                                    display: true,
                                    position: 'right',
                                    beginAtZero: true,
                                    grid: {
                                        drawOnChartArea: false,
                                    },
                                    ticks: {
                                        callback: function (value) {
                                            return formatCurrency(value);
                                        }
                                    }
                                }
                            }
                        }}
                    />
                </div>

                {cashFlowForecast.recommendations.length > 0 && (
                    <Alert
                        message="Khuyến nghị dựa trên dự báo"
                        description={
                            <List
                                size="small"
                                dataSource={cashFlowForecast.recommendations}
                                renderItem={item => <List.Item>• {item}</List.Item>}
                            />
                        }
                        type="info"
                        showIcon
                    />
                )}
            </Card>
        );
    };

    return (
        <div className={styles.advancedCharts}>
            <Card className={styles.controlCard}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <h3>📊 Biểu đồ nâng cao</h3>
                    </Col>
                    <Col>
                        <Select
                            value={selectedChart}
                            onChange={setSelectedChart}
                            style={{ width: 200 }}
                        >
                            <Option value="heatmap">Bản đồ nhiệt</Option>
                            <Option value="trend">Phân tích xu hướng</Option>
                            <Option value="comparison">So sánh tháng</Option>
                            <Option value="forecast">Dự báo dòng tiền</Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {selectedChart === 'heatmap' && renderHeatmap()}
            {selectedChart === 'trend' && renderTrendAnalysis()}
            {selectedChart === 'comparison' && renderMonthComparison()}
            {selectedChart === 'forecast' && renderCashFlowForecast()}
        </div>
    );
}
